import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

type Check = { id: string; name: string; ok: boolean | "warn"; detail: string; fix?: string };
type Report = { mode: string; checks: Check[]; recent_events: any[]; generated_at: string };

const AdminStripeWebhookChecklist = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setLoading(true); setError(null);
    const { data, error } = await supabase.functions.invoke("stripe-webhook-checklist");
    if (error) setError(error.message); else setReport(data as Report);
    setLoading(false);
  };

  useEffect(() => { run(); }, []);

  const passed = report?.checks.filter((c) => c.ok === true).length ?? 0;
  const warned = report?.checks.filter((c) => c.ok === "warn").length ?? 0;
  const failed = report?.checks.filter((c) => c.ok === false).length ?? 0;

  return (
    <AdminLayout title="STRIPE WEBHOOK CHECKLIST">
      <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">
        Live diagnostic checks for webhook configuration, signature verification, idempotency, DB updates, and fallback recovery.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
          Re-run checks
        </Button>
        {report && (
          <>
            <span className="px-3 py-1 text-xs font-mono uppercase border border-border">
              Mode: <span className={report.mode === "live" ? "text-emerald-400" : "text-amber-300"}>{report.mode}</span>
            </span>
            <span className="px-3 py-1 text-xs font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">{passed} pass</span>
            {warned > 0 && <span className="px-3 py-1 text-xs font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300">{warned} warn</span>}
            {failed > 0 && <span className="px-3 py-1 text-xs font-mono border border-rose-500/40 bg-rose-500/10 text-rose-300">{failed} fail</span>}
            <span className="ml-auto text-[10px] font-mono text-muted-foreground">
              {new Date(report.generated_at).toLocaleString()}
            </span>
          </>
        )}
      </div>

      {error && <div className="border border-rose-500/40 bg-rose-500/10 text-rose-200 p-4 mb-4">{error}</div>}

      {loading && !report ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : report ? (
        <div className="bg-panel border border-border divide-y divide-border">
          {report.checks.map((c) => {
            const Icon = c.ok === true ? CheckCircle2 : c.ok === "warn" ? AlertTriangle : XCircle;
            const color = c.ok === true ? "text-emerald-400" : c.ok === "warn" ? "text-amber-300" : "text-rose-400";
            return (
              <div key={c.id} className="p-4 flex items-start gap-3">
                <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${color}`} />
                <div className="flex-1">
                  <div className="font-body text-sm">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">{c.detail}</div>
                  {c.fix && (
                    <div className="text-xs text-amber-200 mt-2 border-l-2 border-amber-400/40 pl-2">
                      <span className="font-mono uppercase tracking-widest opacity-70">Fix · </span>{c.fix}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {report?.recent_events && report.recent_events.length > 0 && (
        <>
          <h3 className="font-display tracking-widest text-sm mt-8 mb-3">RECENT EVENTS</h3>
          <div className="bg-panel border border-border divide-y divide-border text-xs font-mono">
            {report.recent_events.map((e: any) => (
              <div key={e.id} className="p-3 flex items-center gap-3">
                <span className={`px-2 py-0.5 text-[10px] uppercase ${
                  e.status === "processed" ? "bg-emerald-500/15 text-emerald-300" :
                  e.status === "failed" ? "bg-rose-500/15 text-rose-300" : "bg-muted text-muted-foreground"
                }`}>{e.status}</span>
                <span className="flex-1">{e.type}</span>
                <span className="text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminStripeWebhookChecklist;